import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Tab,
  Tabs,
  TextField,
  FormControlLabel,
  Switch,
  Autocomplete,
  Chip,
  Alert,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { ItemEditor } from './ItemEditor';
import {
  useCreateChecklistTemplate,
  useUpdateChecklistTemplate,
  type ChecklistTemplate,
  type ChecklistTemplateItem,
} from '@/hooks/useChecklists';
import { useServices, type Service } from '@/hooks/useServices';
import { useNotify } from '@/stores/notificationStore';
import { describeApiError } from '@/lib/describeApiError';

interface Props {
  open: boolean;
  onClose: () => void;
  template: ChecklistTemplate | null;
}

export function TemplateFormDialog({ open, onClose, template }: Props) {
  const { t } = useTranslation('checklists');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const isEditing = !!template;

  const createTemplate = useCreateChecklistTemplate();
  const updateTemplate = useUpdateChecklistTemplate();
  const { data: services } = useServices({ isActive: true });

  const [tab, setTab] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [items, setItems] = useState<ChecklistTemplateItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description ?? '');
      setIsDefault(template.isDefault);
      setSelectedServiceIds(template.serviceIds ?? []);
      setItems((template.items as ChecklistTemplateItem[]) ?? []);
    } else {
      setName('');
      setDescription('');
      setIsDefault(false);
      setSelectedServiceIds([]);
      setItems([]);
    }
    setTab(0);
    setError(null);
  }, [template, open]);

  const handleSave = async () => {
    setError(null);

    if (!name.trim()) {
      setError(t('template.name'));
      setTab(0);
      return;
    }
    if (items.length === 0) {
      setError(t('template.noItems'));
      setTab(1);
      return;
    }
    const emptyLabels = items.some((item) => !item.label.trim());
    if (emptyLabels) {
      setError(t('item.label'));
      setTab(1);
      return;
    }

    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        isDefault,
        serviceIds: selectedServiceIds.length > 0 ? selectedServiceIds : null,
        items: items.map((item, i) => ({ ...item, sortOrder: i })),
        sortOrder: 0,
      };

      if (isEditing) {
        await updateTemplate.mutateAsync({ id: template.id, ...payload });
        notify.success(t('template.updated'));
      } else {
        await createTemplate.mutateAsync(payload);
        notify.success(t('template.created'));
      }
      onClose();
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.templateSaveFailed') }));
    }
  };

  const selectedServices = (services ?? []).filter((s: Service) => selectedServiceIds.includes(s.id));
  const isPending = createTemplate.isPending || updateTemplate.isPending;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEditing ? t('template.edit') : t('template.create')}
      onSubmit={() => void handleSave()}
      isDirty={name !== '' || items.length > 0}
      isLoading={isPending}
      maxWidth="md"
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, px: 3 }}>
        <Tabs value={tab} onChange={(_e, v: number) => setTab(v)}>
          <Tab label={t('template.tabGeneral')} />
          <Tab label={t('template.tabItems')} />
        </Tabs>
      </Box>

      <Box sx={{ px: 3, pb: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {tab === 0 && (
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label={t('template.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              size="small"
            />
            <TextField
              label={t('template.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              fullWidth
              size="small"
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                />
              }
              label={t('template.isDefault')}
            />
            {isDefault && (
              <Alert severity="info" variant="outlined" sx={{ mt: -1, py: 0 }}>
                {t('template.isDefaultHint')}
              </Alert>
            )}
            <Autocomplete
              multiple
              size="small"
              options={services ?? []}
              getOptionLabel={(option: Service) => option.name}
              value={selectedServices}
              onChange={(_e, newValue) => setSelectedServiceIds(newValue.map((s: Service) => s.id))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('template.serviceLinks')}
                  helperText={t('template.serviceLinksHint')}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option: Service, index) => (
                  <Chip label={option.name} size="small" {...getTagProps({ index })} key={option.id} />
                ))
              }
            />
          </Box>
        )}

        {tab === 1 && (
          <ItemEditor items={items} onChange={setItems} />
        )}
      </Box>
    </FormDialog>
  );
}
